import { useCallback, memo, useEffect, useRef } from "react";
import s from "./FindOrganizationsMap.module.css";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import classNames from "classnames";
import PropTypes from "prop-types";
import RadioButtons from "../RadioButtons/RadioButtons.tsx";
import {
    extendedBranchesDetailsArray,
    getBrancCodeFromGuid,
} from "../../ts/branches";

import mapStyles from "./MapStyles.json";
import CustomDropdown from "../CustomDropdown";
import { useMediaQuery } from "usehooks-ts";

import {
    useFindOrganizationsMapStore,
    useElementSizeStore,
    useMapStore,
} from "./store.jsx";

import MapInputField from "./components/MapInputField";
import ResultsContainer from "./components/ResultsContainer.jsx";
import DEBUGBAR from "./components/DEBUGBAR.jsx";
import MapCheckbox from "./components/MapCheckbox.jsx";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner.tsx";
import TFCluster from "./components/TFCluster.jsx";
import debounce from "../utils/debounce";
import TFInfoBox from "./components/TFInfoBox";

const scrollBarWidth = (() => {
    let div = document.createElement("div");

    div.style.overflowY = "scroll";
    div.style.width = "50px";
    div.style.height = "50px";

    // must put it in the document, otherwise sizes will be 0
    document.body.append(div);
    let scrollWidth = div.offsetWidth - div.clientWidth;

    div.remove();

    return scrollWidth;
})();
const containerStyle = {
    width: `calc(100vw - ${scrollBarWidth}px)`,
    height: "100vh",
    maxHeight: "82rem",
};

const center = {
    lat: 62.3875,
    lng: 16.325556,
};

/*
 *[TODO] TEST - Implementera envs vid build
 */
const GOOGLE_MAPS_API_KEY =
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
    "AIzaSyCdTxqxohfFLl9DpZiol7Z55yciQxxlAyg";

export function FindOrganizationsMap(props) {
    const { isLoaded } = useLoadScript({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        mapId: 'e8e1e8919fbb95f0'
    });
    const {
        inputValue,
        setBransch,
        showMap,
        fairIsChecked,
        getOrganizations,
        setShowSuggestions,
        expandendResults,
        setExpandendResults,
        organizations: organizationsData,
        organizationsLoading: organizationsIsLoading,
        suggestions: suggestionsData,
        suggestionsLoading: suggestionsIsLoading,
        mapPosition,
        setShowIndicators,
        selectedItem,
    } = useFindOrganizationsMapStore();

    const {
        setMenuContainerHeight,
        heightResultContainer,
        menuContainerHeight,
    } = useElementSizeStore();
    const currentlyVisibleMarkers = new Set();

    const { map, setMap, clusterer } = useMapStore();

    const containerRef = useRef(null);
    const isDesktopSize = useMediaQuery("(min-width: 992px)");

    const handleShowIndicators = debounce((map) => {
        setShowIndicators(map.getZoom() > autoZoomLimit);
    }, 20);
    const onLoad = useCallback(
        (map) => {
            setMap(map);
            if(!map) return;
            window.google.maps.event.addListener(map, "bounds_changed", () => handleShowIndicators(map));
        },
        [map],
    );


    const isInBounds = (mapInstance, cluster) => {
        const bounds = mapInstance.getBounds();

        if(!bounds) {
            return true;
        }

        return bounds.contains(cluster.getCenter());
    }

    const boundsChange = debounce((clusterer) => {
            // A temporary set to track the new set of visible markers
            const newVisibleMarkers = new Set();

            const mapInstance = clusterer.getMap();
            // Get the new visible clusters
            const newVisibleClusters = clusterer.getClusters().filter((cluster) => isInBounds(mapInstance, cluster));

            newVisibleClusters.forEach((cluster) => {
                const markers = cluster.getMarkers();
                markers.forEach((marker) => {
                    // Add newly visible markers to the temporary set
                    newVisibleMarkers.add(marker);
                    // If the marker is not already in the currentlyVisibleMarkers set, make it visible
                    if (!currentlyVisibleMarkers.has(marker)) {
                        if (marker.shouldBeHidden) {
                            return;
                        }
                        marker.setVisible(true);
                    }
                });
            });

            // Hide the markers that were visible before but now are outside the bounds
            currentlyVisibleMarkers.forEach((marker) => {
                if (!newVisibleMarkers.has(marker)) {
                    marker.setVisible(false);

                }
            });

            // Replace the currently visible markers set with the new one
            currentlyVisibleMarkers.clear();
            newVisibleMarkers.forEach((marker) => {
                currentlyVisibleMarkers.add(marker);
            });

            clusterer.repaint();
    }, 100);
    

    const defaultZoom = 5;
    const autoZoomLimit = 15;
    const centerOffset = isDesktopSize ? 170 : 0;
    
    useEffect(() => {
        if (!map) return;
        if (!clusterer) return;
        window.google.maps.event.addListener(map, "idle", () => boundsChange(clusterer))
    }, [map, clusterer])

    useEffect(() => {
        if (!map) return;
        if(!selectedItem) return;
        const smoothZoom = (map, newValue, max) => {
            if (newValue > max) {
                offsetCenterByPixels(map, centerOffset, 0);
                return;
            }

            window.google.maps.event.addListenerOnce(
                map,
                "zoom_changed",
                function (event) {
                    setTimeout(() => {
                        smoothZoom(map, newValue + 1, max);
                    }, 100);
                },
            );
            map.setZoom(newValue + 1);
            offsetCenterByPixels(map, centerOffset, 0);
        };

        const currentZoom = map.getZoom();
        const targetEvent = "bounds_changed";
        window.google.maps.event.addListenerOnce(map, targetEvent, () => {
            smoothZoom(map, currentZoom + 1, autoZoomLimit);
        });
        window.google.maps.event.trigger(map, targetEvent);
    }, [mapPosition]);

    const filtredOrganizationsData = organizationsData?.result;

    useEffect(() => {
        if (!!containerRef.current) {
            const containerHeight = containerRef.current.clientHeight;
            setMenuContainerHeight(containerHeight);
        }
    }, [filtredOrganizationsData, expandendResults, showMap, isDesktopSize]);

    const handleOranizationBounds = (organizations) => {
        const bounds = new window.google.maps.LatLngBounds();
        const newBounds = organizations.forEach((item) => {
            if (item?.geoLocation?.latitude && item?.geoLocation?.longitude) {
                const markerPos = new window.google.maps.LatLng(
                    item.geoLocation.latitude,
                    item.geoLocation.longitude,
                );
                bounds.extend(markerPos);
            }
        });

        return bounds;
    };

    useEffect(() => {
        if (
            !isLoaded ||
            !map ||
            !filtredOrganizationsData?.length ||
            filtredOrganizationsData?.every(
                (x) => !x.geoLocation?.latitude || !x.geoLocation?.longitude,
            )
        )
            return;

        const markerBounds = handleOranizationBounds(filtredOrganizationsData);
        window.google.maps.event.addListenerOnce(
            map,
            "bounds_changed",
            function () {
                this.setZoom(Math.min(this.getZoom(), autoZoomLimit)); // overview zoom
            },
        );

        map.fitBounds(markerBounds);
    }, [isLoaded, filtredOrganizationsData, expandendResults]);

    function offsetCenterByPixels(map, offsetX, offsetY) {
        if(offsetX === 0 && offsetY === 0){
            map.panTo(mapPosition);
            return;
        }
        
        const targetCenter = {
            lat: mapPosition.lat,
            lng: mapPosition.lng,
        }
        const scale = Math.pow(2, map.getZoom()); // Number of world pixels per pixel at current zoom level

        const worldCoordinateCenter = map.getProjection().fromLatLngToPoint(targetCenter);
        const pixelOffset = new google.maps.Point((offsetX / scale) || 0, (offsetY / scale) || 0);

        const worldCoordinateNewCenter = new google.maps.Point(
            worldCoordinateCenter.x - pixelOffset.x,
            worldCoordinateCenter.y + pixelOffset.y
        );
        

        const newCenter = map.getProjection().fromPointToLatLng(worldCoordinateNewCenter);
        map.panTo(newCenter); // Pans the map to the new center
    }
    
    const handleSelectBranch = (branch) => {
        setBransch(branch);
        getOrganizations(branch, inputValue, fairIsChecked);
        setShowSuggestions(false);
        setExpandendResults(true);
    };

    const onClusteringEnd = (clusterer) => {
        // Get the current bounds of the map
        const bounds = map.getBounds();
        const slightlyExpandedBounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(
                bounds.getSouthWest().lat() - 0.5,
                bounds.getSouthWest().lng() - 0.5,
            ),
            new window.google.maps.LatLng(
                bounds.getNorthEast().lat() + 0.5,
                bounds.getNorthEast().lng() + 0.5,
            ),
        );

        // Filter out clusters that are outside of the map bounds
        const clustersOutsideMapBounds = clusterer.getClusters().filter((cluster) => {
            return !slightlyExpandedBounds.contains(cluster.center);
        });

        // Hide all markers in these clusters
        clustersOutsideMapBounds.forEach((cluster) => {
            cluster.getMarkers().forEach((marker) => {
                marker.setVisible(false);
            });
        });
    };

    const getBranchIconsUrl = (icon) =>
        `/icons/burgundy-orange/markers/${icon}.svg`;

    const handleOrganizationIcons = (branches) => {
        const branchCode =
            branches === undefined || branches.length > 1 || !branches.length
                ? "TFALL"
                : getBrancCodeFromGuid(branches[0]);
        return getBranchIconsUrl(branchCode);
    };

    const controllerClasses = classNames(s.Controller, {});

    const menuWrapperClasses = classNames(s.MenuWrapper, {
        [s.Expanded]:
        (expandendResults && isDesktopSize) ||
        (!showMap && expandendResults && isDesktopSize),
        [s.AutoHeight]:
        !filtredOrganizationsData?.length > 0 ||
        (heightResultContainer + menuContainerHeight < 710 &&
            !window.innerHeight > 832 &&
            isDesktopSize),
        [s.OverflowScroll]: !isDesktopSize && !showMap,
    });
    const onUnmount = useCallback((map) => {
        if (!map) return;
        
        setMap(null);
    }, []);

    return (
        <div className={s.root}>
            {!!isLoaded ? (
                <>
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={defaultZoom}
                        spiderfyOnMaxZoom={true}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{
                            fullscreenControl: true,
                            mapTypeControl: false,
                            streetViewControl: false,
                            scrollwheel: false,

                            zoomControl: true,
                            trackResize: true,
                            gestureHandling: "greedy",
                            pinchZoom: true,
                        }}
                    >
                        <div className={menuWrapperClasses}>
                            <div
                                className={controllerClasses}
                                ref={containerRef}
                            >
                                <MapInputField />
                                <CustomDropdown
                                    label={"Välj bransch"}
                                    className={s.margin_bottom}
                                    onChange={(e) => e.preventDefault()}
                                    options={extendedBranchesDetailsArray}
                                    setBranch={handleSelectBranch}
                                />

                                <MapCheckbox />
                                {!isDesktopSize && (
                                    <RadioButtons variant="map-list-toggle" />
                                )}
                                {!!filtredOrganizationsData?.length && (
                                    <div className={s.ShowingNumbersTitle}>
                                        Visar{" "}
                                        <span
                                            className={s.ShowingNumbersNumber}
                                        >
                                            {filtredOrganizationsData.length}
                                        </span>{" "}
                                        medlemsföretag
                                    </div>
                                )}
                            </div>
                            {!organizationsIsLoading &&
                                filtredOrganizationsData?.length > 0 && (
                                    <TFCluster
                                        onClusteringEnd={onClusteringEnd}
                                        handleOrganizationIcons={
                                            handleOrganizationIcons
                                        }
                                    />
                                )}

                            <TFInfoBox item={selectedItem}/>
                            <ResultsContainer />
                        </div>
                        {organizationsIsLoading && (
                            <LoadingSpinner backdrop={true} />
                        )}
                    </GoogleMap>
                </>
            ) : (
                <></>
            )}

            {props.debug && <DEBUGBAR />}
        </div>
    );
}

FindOrganizationsMap.propTypes = {
    organizations: PropTypes.array,
    debug: PropTypes.bool,
};

export default memo(FindOrganizationsMap);
